/* eslint-disable complexity */
import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Icon20Chain } from '@vkontakte/icons';
import { downloadAsJpeg } from 'Cloud/Application/DownloadAsJpeg';
import classNames from 'clsx';
import React, { type VFC, memo, useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { animateNewTableGrid } from 'reactApp/appHelpers/featuresHelpers/features/datalistNewTableGrid';
import { enabledPublicDownloadLimits, tooltipTrackDownload } from 'reactApp/appHelpers/featuresHelpers/features/publicDownloadLimits';
import DownloadCounter from 'reactApp/components/DownloadCounter/DownloadCounter';
import { useRenameInlineDoubleClick } from 'reactApp/hooks/useRenameInlineDoubleClick';
import { getWeblinkFromPublicId } from 'reactApp/modules/file/utils';
import { getOwner } from 'reactApp/modules/folderAccessControlList/folderAccessControlList.selectors';
import { EInlineEditPlace } from 'reactApp/modules/modifying/modifying.types';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getFirstPublicWithDownloadLimits, getPublicLimits } from 'reactApp/modules/public/public.selectors';
import { getCurrentRouteId } from 'reactApp/modules/router/router.selectors';
import { EViewMode } from 'reactApp/modules/settings/settings.types';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import { AiPhotoImprovingTooltip } from 'reactApp/ui/AiPhotoImprovingTooltip/AiPhotoImprovingTooltip';
import { Avatar } from 'reactApp/ui/Avatar/Avatar';
import { DataListCheckbox } from 'reactApp/ui/DataListCheckbox/DataListCheckbox';
import { DataListControl } from 'reactApp/ui/DataListControl/DataListControl';
import { AiOrganize } from 'reactApp/ui/DataListItemCommonComponents/AiOrganize/AiOrganize';
import { Button } from 'reactApp/ui/DataListItemCommonComponents/Button/Button';
import { Progress } from 'reactApp/ui/DataListItemRow/components/Progress';
import { FloatingTooltip } from 'reactApp/ui/FloatingTooltip/FloatingTooltip';
import { Hint } from 'reactApp/ui/Hint/Hint';

import { ETooltipPlacement, ETooltipSize } from '../FloatingTooltip/FloatingTooltip.types';
import { TrackDownloadCountPromo } from '../SharingTooltips/TrackDownloadCountPromo';
import { DataListItemThumbMeta } from './components/DataListItemThumbMeta';
import { Pic } from './components/Pic';
import { Publish } from './components/Publish';
import styles from './DataListItemThumb.css';
import type { DataListNewItemThumbProps } from './DataListItemThumb.types';
/** tempexp_14729-next-line */
import { DataListItemThumb as DataListItemThumbNew } from './new/DataListItemThumb';

const getDownloadHint = (ext?: string, size?: number) =>
    ext
        ? `Скачать .${ext.toLowerCase()}${size ? `, ${bytesToNDigits(size, 3).value}` : ''}`
        : `Скачать${size ? ` ${bytesToNDigits(size, 3).value}` : ''}`;

// eslint-disable-next-line max-lines-per-function
export const DataListItemThumbComponent: VFC<DataListNewItemThumbProps> = (props) => {
    const {
        isSelected,
        isActive,
        isSelecting,
        forceCheckbox,
        onCheckboxClick,
        size,
        isFolder,
        name,
        ext,
        tipData,
        id,
        denyDoAction,
        deniedByRights,
        progress,
        storage,
        isPublicDownloaded,
        isPopup,
        parentItemRef,
        isSeoEnabled,
        weblink,
        showPublishButton,
        isIncomingPublicOrShared,
        onAuthorContextMenuItem,
        isShowAiTooltip,
        isShowAiPhotoImprovingTooltip,
        onPhotoImprovingOpen,
    } = props;

    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const [showTooltipCounter, setShowTooltipCounter] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const hasPublishButton = storage === EStorageType.attaches && !!props.onPublish;
    const isRenameAvailable = 'renameAvailable' in props && props.renameAvailable;
    const { isEdit, handleDoubleClick, onRename } = useRenameInlineDoubleClick(name, id, EInlineEditPlace.datalistThumbs, ext);

    // tempexp_17847-start
    const isPaidUser = useSelector(UserSelectors.isPaidUser);
    const routeId = useSelector(getCurrentRouteId);
    const weblinkUrl = getWeblinkFromPublicId(routeId);
    const downloadLimits = useSelector((state: RootState) => getPublicLimits(state, weblinkUrl || weblink || ''));
    const downloadLimit = downloadLimits && downloadLimits.total;
    const downloadLeft = downloadLimits && downloadLimits.left;
    const downloads = downloadLimit - downloadLeft;
    const showDownloadButton =
        props.showDownloadButton && !denyDoAction && !forceCheckbox && !isPopup && !(storage === EStorageType.public && isPublicDownloaded);

    const showDownloadCounter =
        enabledPublicDownloadLimits &&
        (storage === EStorageType.home || storage === EStorageType.sharedLinks) &&
        !isPaidUser &&
        downloads > 0;

    const firstPublicWithDownloads = useSelector((state: RootState) => getFirstPublicWithDownloadLimits(state));
    const weblinkForPromo = firstPublicWithDownloads && Object.keys(firstPublicWithDownloads)[0];
    const showTrackDownloadCountPromo = tooltipTrackDownload && showDownloadCounter;
    const showFirstTrackDownloadCountPromo = showTrackDownloadCountPromo && weblink === weblinkForPromo;

    const currentUserEmail = useSelector(UserSelectors.getEmail);
    const owner = useSelector(getOwner);
    const isOwner = currentUserEmail === owner?.email;
    const isAllDocumentsAccessDenied = deniedByRights && storage === EStorageType.alldocuments;

    const TOOLTIP_ID = 'tooltipTrackDownload';
    const isTooltipShown = Boolean(storeHelper.getValue(TOOLTIP_ID));

    const handleCounterClick = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowTooltipCounter(true);
    };

    const handleCounterPromoHide = () => {
        setShowTooltipCounter(false);
    };
    // tempexp_17847-end

    const handleDropdown = useCallback(
        (isOpen) => {
            if (denyDoAction || deniedByRights) {
                return null;
            }

            setDropdownOpen(isOpen);
        },
        [setDropdownOpen, denyDoAction, deniedByRights]
    );

    const getOwnerNameAndMail = (item) => {
        if (item.owner) {
            return {
                name: item.owner.name || item.owner.email,
                email: item.owner.email,
            };
        } else if (item.author) {
            return {
                name: item?.ownerName || item?.author,
                email: item?.author,
            };
        }

        return null;
    };

    const renderAuthor = (isShared?: boolean) => {
        const ownerData = getOwnerNameAndMail(props);
        const onContextMenu = isIncomingPublicOrShared ? onAuthorContextMenuItem : undefined;
        // для пошаренных автор для ЛКМ, в др. местах ПКМ
        const clickHandler = isShared ? { onClick: onContextMenu } : { onContextMenu };

        if (!ownerData) {
            return null;
        }

        return (
            <>
                <Hint text={ownerData.name}>
                    <div className={styles.owner} {...clickHandler}>
                        <Avatar email={ownerData.email} name={ownerData.name} size={30} />
                    </div>
                </Hint>
                {isShowAiTooltip && storage === EStorageType.attaches && (
                    <AiOrganize
                        itemId={id}
                        animate={animateNewTableGrid}
                        viewType={EViewMode.thumbs}
                        size={30}
                        storage={EStorageType.attaches}
                    />
                )}
            </>
        );
    };

    const renderFooter = () => {
        switch (storage) {
            case EStorageType.home:
            case EStorageType.sharedLinks:
            case EStorageType.sharedAutodelete:
            case EStorageType.favorites:
            case EStorageType.search:
            case EStorageType.feed:
            case EStorageType.documents:
            case EStorageType.gallery:
            case EStorageType.alldocuments:
                if (props.kind === 'family') {
                    return;
                }

                if (storage === EStorageType.home && denyDoAction) {
                    return <Progress className={styles.progress} progress={progress} onClick={props.onCancelUpload} />;
                }

                if (isIncomingPublicOrShared) {
                    return renderAuthor(true);
                }

                return (
                    <>
                        <Hint text="Поделиться ссылкой">
                            <div>
                                {showPublishButton && (
                                    <Publish
                                        isActive={props.isPublic}
                                        onClick={props.onPublish}
                                        isSeoEnabled={isSeoEnabled}
                                        showPublicButton={storage === EStorageType.home && props.showPublicButton}
                                        itemId={id}
                                    />
                                )}
                            </div>
                        </Hint>
                        {isShowAiTooltip && (
                            <AiOrganize
                                itemId={id}
                                rootClassName={props.isPublic ? styles.aiControl_displaced : styles.aiControl}
                                animate={animateNewTableGrid}
                                viewType={EViewMode.thumbs}
                                noTip={!!tipData}
                                size={28}
                                storage={storage}
                            />
                        )}
                        <div ref={tooltipRef}>
                            {showDownloadCounter && (
                                <DownloadCounter downloads={downloads} downloadLimit={downloadLimit} onClick={handleCounterClick} />
                            )}
                        </div>
                        {showFirstTrackDownloadCountPromo && !isTooltipShown && (
                            <TrackDownloadCountPromo targetRef={tooltipRef} downloads={downloads} downloadLimit={downloadLimit} />
                        )}
                        {showTrackDownloadCountPromo && showTooltipCounter && (
                            <TrackDownloadCountPromo
                                targetRef={tooltipRef}
                                downloads={downloads}
                                downloadLimit={downloadLimit}
                                onHide={handleCounterPromoHide}
                            />
                        )}
                    </>
                );
            case EStorageType.incomingPublic:
            case EStorageType.attaches:
            case EStorageType.public: {
                return renderAuthor(storage === EStorageType.incomingPublic);
            }
        }
    };

    const renderButtons = () => {
        if (!hasPublishButton) {
            return null;
        }

        return (
            <Button onClick={props.onPublish}>
                Поделиться
                <Icon20Chain height={15} width={15} />
            </Button>
        );
    };

    const renderDownloadButton = () => {
        if (!showDownloadButton) {
            return null;
        }

        const downloadHint = getDownloadHint(ext, size);

        return ext && downloadAsJpeg.isAvailable({ ext, storage }) ? (
            <DataListControl
                type="downloadAsJpeg"
                onClick={props.onDownload}
                onDropdown={handleDropdown}
                list={downloadAsJpeg.getDropdownItems({
                    itemOrId: id,
                    gaSuffix: 'datalist',
                    ext,
                    textPrefix: 'в',
                    storage: props.storage,
                })}
                hint={downloadHint}
                viewType={EViewMode.thumbs}
            />
        ) : downloadLeft !== 0 || isOwner ? (
            <Hint text={downloadHint}>
                <div>
                    <DataListControl type="download" onClick={props.onDownload} viewType={EViewMode.thumbs} />
                </div>
            </Hint>
        ) : null;
    };

    return (
        <div
            ref={parentItemRef}
            className={classNames({
                [styles.root]: true,
                [styles.root_selected]: isSelected || isDropdownOpen,
                [styles.root_active]: isActive,
                [styles.root_disabled]: isAllDocumentsAccessDenied,
                [styles.root_selecting]: isSelecting,
            })}
        >
            {!isPopup && (
                <div className={styles.pinLeft}>
                    {!!tipData && (
                        <FloatingTooltip
                            target={parentItemRef}
                            className={styles.tooltip}
                            size={ETooltipSize.large}
                            text={tipData[0].getTipContent?.(isFolder)}
                            placement={ETooltipPlacement.top}
                            closeOnOutsideClick={false}
                            qaId={tipData[0].id}
                            onClose={tipData[0].onHideClick}
                            usePortal
                        />
                    )}
                </div>
            )}
            <div className={styles.pin}>
                {renderDownloadButton()}
                {(forceCheckbox || isPopup) && (
                    <Hint text={isSelected ? 'Снять выделение' : 'Выделить'}>
                        <div onClick={onCheckboxClick}>
                            <DataListCheckbox checked={isSelected} />
                        </div>
                    </Hint>
                )}
            </div>
            <div className={styles.content}>
                {isShowAiPhotoImprovingTooltip && (
                    <div className={styles.aiIconContainer}>
                        <AiPhotoImprovingTooltip size={24} onPhotoImprovingOpen={onPhotoImprovingOpen} />
                    </div>
                )}
                <Pic
                    isFolder={props.isFolder}
                    thumbUrl={props.thumbUrl}
                    short={hasPublishButton}
                    kind={props.kind}
                    isVirus={'isVirus' in props && Boolean(props.isVirus)}
                    isAutoDelete={'isAutoDelete' in props && props.isAutoDelete}
                    ext={props.ext || ''}
                    isLoading={denyDoAction}
                />
            </div>
            <div
                className={classNames(styles.footer, {
                    [styles.footerEdit]: isEdit,
                })}
            >
                {!isPopup && <div className={styles.controls}>{renderFooter()}</div>}
                <DataListItemThumbMeta
                    {...props}
                    isRenameAvailable={isRenameAvailable}
                    isEdit={isEdit}
                    isAllDocumentsAccessDenied={isAllDocumentsAccessDenied}
                    onRename={onRename}
                    handleDoubleClick={handleDoubleClick}
                />
                {renderButtons()}
            </div>
        </div>
    );
};

/** tempexp_14729-next-line */
export const DataListItemThumb = Object.assign(memo(DataListItemThumbComponent), { New: DataListItemThumbNew });
DataListItemThumb.displayName = 'DataListItemThumb';
DataListItemThumb.whyDidYouRender = true;
